/**
 * @fileoverview Tags API service and TanStack Query hooks.
 * @module features/tags/api/tags
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'
import { getProducts } from '@/lib/productService'
import {
  tagSchema,
  productSummarySchema,
  type Tag,
  type ProductSummary,
} from '../schemas/tagSchema'

export const TAGS_QUERY_KEY = ['tags'] as const
export const tagProductsQueryKey = (
  tagUuid: string
): readonly ['tags', string, 'products'] => ['tags', tagUuid, 'products'] as const
export const productTagsQueryKey = (
  productExternalId: string
): readonly ['tags', 'product', string] => ['tags', 'product', productExternalId] as const

export interface TagInput {
  name: string
}

export const tagsService = {
  async getTags(): Promise<Tag[]> {
    const res = await apiClient.get('/tags')
    return tagSchema.array().parse(res.data)
  },
  async createTag(input: TagInput): Promise<Tag> {
    const res = await apiClient.post('/tags', input)
    return tagSchema.parse(res.data)
  },
  async updateTag(uuid: string, input: TagInput): Promise<Tag> {
    const res = await apiClient.put(`/tags/${uuid}`, input)
    return tagSchema.parse(res.data)
  },
  async deleteTag(uuid: string): Promise<void> {
    await apiClient.delete(`/tags/${uuid}`)
  },
  async getTagProducts(tagUuid: string): Promise<ProductSummary[]> {
    const res = await apiClient.get(`/tags/${tagUuid}/products`)
    return productSummarySchema.array().parse(res.data)
  },
  async setTagProducts(tagUuid: string, productExternalIds: string[]): Promise<void> {
    await apiClient.put(`/tags/${tagUuid}/products`, { productExternalIds })
  },
  async getProductTags(productExternalId: string): Promise<Tag[]> {
    const res = await apiClient.get(`/tags/products/${productExternalId}`)
    return tagSchema.array().parse(res.data)
  },
  async setProductTags(productExternalId: string, tagUuids: string[]): Promise<void> {
    await apiClient.put(`/tags/products/${productExternalId}`, { tagUuids })
  },
}

export function useTagsQuery(options?: { enabled?: boolean }): UseQueryResult<Tag[], Error> {
  return useQuery<Tag[], Error>({
    queryKey: TAGS_QUERY_KEY,
    queryFn: () => tagsService.getTags(),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  })
}

export function useCreateTagMutation(): UseMutationResult<Tag, Error, TagInput> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: input => tagsService.createTag(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TAGS_QUERY_KEY })
    },
  })
}

export function useUpdateTagMutation(): UseMutationResult<
  Tag,
  Error,
  { uuid: string; input: TagInput }
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, input }) => tagsService.updateTag(uuid, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TAGS_QUERY_KEY })
    },
  })
}

export function useDeleteTagMutation(): UseMutationResult<void, Error, { uuid: string }> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid }) => tagsService.deleteTag(uuid),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: TAGS_QUERY_KEY })
      // Tag deletion resurfaces URL→tag mappings into the inbox.
      await qc.invalidateQueries({ queryKey: ['staging-external-urls'] })
      await qc.invalidateQueries({ queryKey: ['statistics'] })
    },
  })
}

export function useTagProductsQuery(
  tagUuid: string | null
): UseQueryResult<ProductSummary[], Error> {
  return useQuery<ProductSummary[], Error>({
    queryKey: tagProductsQueryKey(tagUuid ?? '_'),
    queryFn: () => tagsService.getTagProducts(tagUuid as string),
    enabled: !!tagUuid,
  })
}

export function useSetTagProductsMutation(): UseMutationResult<
  void,
  Error,
  { tagUuid: string; productExternalIds: string[] }
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ tagUuid, productExternalIds }) =>
      tagsService.setTagProducts(tagUuid, productExternalIds),
    onSuccess: async (_d, { tagUuid }) => {
      await qc.invalidateQueries({ queryKey: tagProductsQueryKey(tagUuid) })
      await qc.invalidateQueries({ queryKey: TAGS_QUERY_KEY })
      await qc.invalidateQueries({ queryKey: ['statistics'] })
    },
  })
}

export function useProductTagsQuery(
  productExternalId: string | null
): UseQueryResult<Tag[], Error> {
  return useQuery<Tag[], Error>({
    queryKey: productTagsQueryKey(productExternalId ?? '_'),
    queryFn: () => tagsService.getProductTags(productExternalId as string),
    enabled: !!productExternalId,
  })
}

export function useSetProductTagsMutation(): UseMutationResult<
  void,
  Error,
  { productExternalId: string; tagUuids: string[] }
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ productExternalId, tagUuids }) =>
      tagsService.setProductTags(productExternalId, tagUuids),
    onSuccess: async (_d, { productExternalId }) => {
      await qc.invalidateQueries({ queryKey: productTagsQueryKey(productExternalId) })
      await qc.invalidateQueries({ queryKey: TAGS_QUERY_KEY })
      await qc.invalidateQueries({ queryKey: ['statistics'] })
    },
  })
}

/**
 * Product candidates for the tag-membership editor. A `Product.id` is the
 * external id used everywhere as `ProductSummary.id`, so the shapes align.
 */
export function useProductCandidatesQuery(
  query: string,
  options?: { enabled?: boolean }
): UseQueryResult<ProductSummary[], Error> {
  return useQuery<ProductSummary[], Error>({
    queryKey: ['tags', 'product-candidates', query],
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      // `sort: 'revenue'` is the only backend branch that honours the `query`
      // param (full-text search); `sort: 'name'` silently ignores it, which made
      // the picker show every product regardless of what was typed.
      const res = await getProducts({
        page: 0,
        size: 20,
        sort: 'revenue',
        ...(query ? { query } : {}),
      })
      return res.content.map(product => ({
        id: product.id,
        name: product.name,
        imageUrl: product.imageUrl ?? null,
      }))
    },
    staleTime: 60 * 1000,
  })
}

export default tagsService
